// src/components/OverrideForm.tsx — admin override (non-empty reason required).
import { useState } from "react";
import { useCreateOverride } from "@/lib/queries";
import { Card, Button, ErrorBanner } from "./ui";

type TargetType = "trust_profile" | "guardian_link" | "participant";

export function OverrideForm() {
  const create = useCreateOverride();
  const [targetType, setTargetType] = useState<TargetType>("participant");
  const [targetId, setTargetId] = useState("");
  const [field, setField] = useState("");
  const [reason, setReason] = useState("");

  const reasonValid = reason.trim().length > 0;
  const idValid = /^\d+$/.test(targetId) && Number(targetId) > 0;
  const canSubmit = reasonValid && idValid && field.trim().length > 0 && !create.isPending;

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      { targetType, targetId: Number(targetId), field: field.trim(), reason: reason.trim() },
      {
        onSuccess: () => {
          setTargetId("");
          setField("");
          setReason("");
        },
      }
    );
  }

  return (
    <Card>
      {create.isError ? (
        <div className="mb-3">
          <ErrorBanner message={(create.error as Error).message} />
        </div>
      ) : null}
      {create.isSuccess ? (
        <p className="mb-3 text-sm text-green-400">Override recorded and audited.</p>
      ) : null}
      <form className="grid grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={submit}>
        <label className="flex flex-col gap-1 text-xs text-ls-muted">
          Target type
          <select
            value={targetType}
            onChange={(e) => setTargetType(e.target.value as TargetType)}
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          >
            <option value="participant">participant</option>
            <option value="trust_profile">trust_profile</option>
            <option value="guardian_link">guardian_link</option>
          </select>
        </label>
        <label className="flex flex-col gap-1 text-xs text-ls-muted">
          Target id
          <input
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            placeholder="123"
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ls-muted">
          Field
          <input
            value={field}
            onChange={(e) => setField(e.target.value)}
            placeholder="e.g. is_minor"
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ls-muted">
          Reason (required)
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this override is necessary"
            className="rounded-lg border border-white/15 bg-transparent px-3 py-2 text-sm text-ls-fg outline-none focus:border-ls-accent"
          />
        </label>
        <div className="sm:col-span-2">
          <Button type="submit" disabled={!canSubmit}>
            {create.isPending ? "Recording…" : "Record override"}
          </Button>
          {!reasonValid && reason.length > 0 ? (
            <span className="ml-3 text-xs text-red-300">reason must be non-empty</span>
          ) : null}
        </div>
      </form>
    </Card>
  );
}
